import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type Lang = 'fr' | 'en' | 'is';

export interface Translations {
  [key: string]: string;
}

const FR: Translations = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.saisie': 'Saisie',
  'nav.qualitatif': 'Qualitatif',
  'nav.cibles': 'Cibles',
  'nav.specialistes': 'Spécialistes',
  'nav.documentation': 'Documentation',
  'nav.perfGroup': 'Revue de performance',

  // Dashboard
  'dashboard.title': 'Revue de performance COA',
  'dashboard.lastUpdate': 'Dernière mise à jour',
  'dashboard.monthlyData': 'Données mensuelles',
  'dashboard.sector': 'Secteur',
  'dashboard.global': 'Global',
  'dashboard.trends': 'Tendances mensuelles',
  'dashboard.qualSummary': 'Résumé qualitatif',

  // Sector view
  'sector.back': '← Retour',
  'sector.history': 'Historique par thème',
  'sector.clickHint': '(cliquez pour voir les KPI)',
  'sector.showKpi': '▼ Voir les KPI',
  'sector.hideKpi': '▲ Masquer les KPI',
  'sector.detailByKpi': 'Détail par KPI',
  'sector.exportPptx': '📊 Exporter PPTX',
  'sector.exportPdf': '📄 Exporter PDF',
  'sector.exporting': 'Export en cours...',
  'sector.qualSections': 'Sections qualitatives',

  // Statuts
  'status.conforme': 'Conforme',
  'status.aSurveiller': 'À surveiller',
  'status.critique': 'Critique',

  // Qualitative
  'qual.enjeux': 'Enjeux/Actions en cours',
  'qual.bonsCoups': 'Bons coups',
  'qual.demandeAide': "Demande d'aide",
  'qual.noEntries': 'Aucune entrée pour cette période.',

  // Documentation
  'doc.title': 'Documentation — Thèmes et KPI',
  'doc.subtitle': 'Ce document présente les thèmes de performance et les indicateurs clés (KPI) utilisés dans la revue de performance COA.',
  'doc.overview': 'Vue d\'ensemble',
  'doc.sectors': 'Secteurs',
  'doc.themes': 'Thèmes',
  'doc.kpis': 'KPI',
  'doc.legend': 'Légende des statuts',
  'doc.methodology': 'Méthodologie de calcul',

  // Months
  'months.january': 'Janvier',
  'months.february': 'Février',
  'months.march': 'Mars',
  'months.april': 'Avril',
  'months.may': 'Mai',
  'months.june': 'Juin',
  'months.july': 'Juillet',
  'months.august': 'Août',
  'months.september': 'Septembre',
  'months.october': 'Octobre',
  'months.november': 'Novembre',
  'months.december': 'Décembre',

  // Qualitative empty states
  'qual.enjeux.empty': 'Aucun enjeu pour cette période.',
  'qual.bonsCoups.empty': 'Aucun bon coup pour cette période.',
  'qual.demandeAide.empty': 'Aucune demande d\'aide pour cette période.',

  // Saisie
  'saisie.title': 'Saisie des mesures',
  'saisie.sector': 'Secteur',
  'saisie.month': 'Mois',
  'saisie.week': 'Semaine',
  'saisie.save': 'Enregistrer',
  'saisie.saved': 'Enregistré',
  'saisie.value': 'Valeur',

  // Qualitative page
  'qual.title': 'Sections qualitatives',
  'qual.add': 'Ajouter',
  'qual.edit': 'Modifier',
  'qual.delete': 'Supprimer',
  'qual.titre': 'Titre',
  'qual.description': 'Description',
  'qual.criticality': 'Criticité',
  'qual.urgency': 'Urgence',
  'qual.status': 'Statut',
  'qual.theme': 'Thème',

  // User Management
  'users.title': 'Gestion des utilisateurs',
  'users.subtitle': 'Gérer les utilisateurs et leurs groupes Cognito',
  'users.addTitle': 'Ajouter un utilisateur',
  'users.email': 'Email',
  'users.tempPassword': 'Mot de passe temporaire',
  'users.add': '+ Ajouter',
  'users.creating': 'Création...',
  'users.count': 'Utilisateurs',
  'users.status': 'Statut',
  'users.groups': 'Groupes',
  'users.createdAt': 'Créé le',
  'users.actions': 'Actions',
  'users.delete': 'Supprimer',
  'users.confirmDelete': 'Confirmer la suppression',
  'users.confirmDeleteMsg': 'Êtes-vous sûr de vouloir supprimer',
  'users.irreversible': 'Cette action est irréversible.',
  'users.cancel': 'Annuler',
  'users.deleting': 'Suppression...',
  'users.emailPlaceholder': 'prenom.nom@riotinto.com',
  'users.emailError': "L'email doit se terminer par @riotinto.com",
  'users.createSuccess': 'Utilisateur créé avec succès',
  'users.createError': 'Erreur lors de la création',
  'users.statusConfirmed': 'Confirmé',
  'users.statusForceChange': 'Changement MDP',
  'users.statusUnconfirmed': 'Non confirmé',
  'users.addGroup': '+ Groupe',
  'users.assignGroup': 'Assigner',
  'users.userExists': 'Cet utilisateur existe déjà',

  // Definitions
  'definitions.selectSector': 'Veuillez sélectionner un secteur pour voir les définitions des KPI.',

  // Adhesion Matrix — Navigation
  'adhesion.nav.group': 'Matrice d\'adhésion',
  'adhesion.nav.global': 'Matrice globale',
  'adhesion.nav.globalSite': 'Matrice par site',
  'adhesion.nav.sector': 'Matrice par secteur',
  'adhesion.nav.evaluation': 'Évaluation',
  'adhesion.nav.lastEval': 'Dernière évaluation',
  'adhesion.nav.documentation': 'Documentation',
  'adhesion.nav.actions': 'Actions en cours',
  'adhesion.nav.admin': 'Administration',

  // Adhesion Matrix — Global
  'adhesion.global.title': 'Matrice d\'adhésion COA',
  'adhesion.global.period': 'Période d\'évaluation',
  'adhesion.global.print': 'Imprimer',
  'adhesion.global.level3': 'Niveau 3',
  'adhesion.global.level2': 'Niveau 2',
  'adhesion.global.level1': 'Niveau 1',
  'adhesion.global.level0': 'Niveau 0',
  'adhesion.global.notEval': 'Non évalué',
  'adhesion.global.legend': 'Légende des critères',

  // Adhesion Matrix — Sector
  'adhesion.sector.title': 'Matrice d\'adhésion par secteur',
  'adhesion.sector.selectSector': 'Sélectionnez un secteur',
  'adhesion.sector.selectHint': 'Choisissez un secteur ci-dessus pour visualiser la matrice d\'adhésion par site.',
  'adhesion.sector.site': 'Site',
  'adhesion.sector.scoreByAxis': 'Score par axe',

  // Adhesion Matrix — Evaluation
  'adhesion.eval.title': 'Évaluation d\'adhésion',
  'adhesion.eval.save': 'Sauvegarder',
  'adhesion.eval.saving': 'Sauvegarde...',
  'adhesion.eval.saved': 'Sauvegardé',
  'adhesion.eval.selectHint': 'Sélectionnez un secteur et une période pour commencer l\'évaluation.',
  'adhesion.eval.addComment': 'Ajouter un commentaire',
  'adhesion.eval.comment': 'Commentaire',
  'adhesion.eval.cancel': 'Annuler',
  'adhesion.eval.register': 'Enregistrer',

  // Adhesion Matrix — Last Evaluation
  'adhesion.lastEval.title': 'Dernière évaluation',
  'adhesion.lastEval.allSectors': 'Tous les secteurs',
  'adhesion.lastEval.thisMonth': 'Ce mois',
  'adhesion.lastEval.months': 'mois',
  'adhesion.lastEval.loading': 'Chargement...',

  // Adhesion Matrix — Actions
  'adhesion.actions.title': 'Actions en cours',
  'adhesion.actions.subtitle': 'Suivi des actions par secteur et domaine d\'adhésion',
  'adhesion.actions.allSectors': 'Tous les secteurs',
  'adhesion.actions.inProgress': 'actions en cours',
  'adhesion.actions.showCompleted': 'Voir complétées',
  'adhesion.actions.hideCompleted': 'Masquer complétées',
  'adhesion.actions.newAction': '+ Nouvelle action',
  'adhesion.actions.add': '+ Ajouter',
  'adhesion.actions.empty': 'Aucune action active pour ce secteur.',
  'adhesion.actions.edit': 'Modifier',
  'adhesion.actions.delete': 'Supprimer cette action ?',
  'adhesion.actions.form.title': 'Nouvelle action',
  'adhesion.actions.form.editTitle': 'Modifier l\'action',
  'adhesion.actions.form.sector': 'Secteur (groupe)',
  'adhesion.actions.form.site': 'Site (spécifique)',
  'adhesion.actions.form.axis': 'Axe (domaine)',
  'adhesion.actions.form.actionTitle': 'Titre',
  'adhesion.actions.form.description': 'Description',
  'adhesion.actions.form.responsible': 'Responsable',
  'adhesion.actions.form.deadline': 'Échéance',
  'adhesion.actions.form.priority': 'Priorité',
  'adhesion.actions.form.status': 'Statut',
  'adhesion.actions.form.save': 'Enregistrer',
  'adhesion.actions.form.cancel': 'Annuler',
  'adhesion.actions.priority.low': 'Faible',
  'adhesion.actions.priority.medium': 'Moyen',
  'adhesion.actions.priority.high': 'Élevé',
  'adhesion.actions.status.new': 'Nouveau',
  'adhesion.actions.status.inProgress': 'En cours',
  'adhesion.actions.status.toConfirm': 'À confirmer',
  'adhesion.actions.status.resolved': 'Résolu',

  // Adhesion Matrix — Admin
  'adhesion.admin.title': 'Administration — Matrice d\'adhésion',
  'adhesion.admin.subtitle': 'Gérez les sites, secteurs, périodes et la disposition de la matrice globale.',
  'adhesion.admin.sites': 'Sites',
  'adhesion.admin.sectors': 'Secteurs',
  'adhesion.admin.periods': 'Périodes',
  'adhesion.admin.sections': 'Sections matrice',
  'adhesion.admin.add': '+ Ajouter',
  'adhesion.admin.delete': 'Supprimer',
  'adhesion.admin.confirmDelete': 'Confirmer la suppression',
  'adhesion.admin.deleteWarning': 'Cette action est irréversible et supprimera toutes les données associées.',
  'adhesion.admin.save': 'Sauvegarder la disposition',
  'adhesion.admin.saved': 'Sauvegardé',
  'adhesion.admin.newPeriod': '+ Nouvelle période',
  'adhesion.admin.close': 'Fermer',
  'adhesion.admin.open': 'Ouverte',
  'adhesion.admin.closed': 'Fermée',

  // Product Review Module
  'productReview.nav.group': 'Revue Perf. Produit',
  'productReview.nav.dashboard': 'Dashboard',
  'productReview.nav.saisie': 'Saisie',
  'productReview.nav.qualitatif': 'Qualitatif',
  'productReview.nav.cibles': 'Cibles',
  'productReview.nav.definitions': 'Définitions KPI',
  'productReview.nav.documentation': 'Documentation',
  'productReview.dashboard.title': 'Revue de Performance Produit',
  'productReview.product': 'Produit',
  'productReview.theme': 'Thème',
  'productReview.trends': 'Tendances mensuelles par thème',
  'productReview.monthlyData': 'Données mensuelles',
  'productReview.back': '← Retour',
  'productReview.scoresByTheme': 'Scores par thème',
  'productReview.detailByKpi': 'Détail par KPI',
  'productReview.scoresByProduct': 'Scores par produit',
  'productReview.trendsByProduct': 'Tendances par produit',
  'productReview.dataEntry.title': 'Saisie des données mensuelles',
  'productReview.targets.title': 'Gestion des cibles',
  'productReview.definitions.title': 'Définitions des KPI',
  'productReview.qualitative.title': 'Données qualitatives',
  'productReview.documentation.title': 'Documentation — Revue de Performance Produit',
};

const EN: Translations = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.saisie': 'Data Entry',
  'nav.qualitatif': 'Qualitative',
  'nav.cibles': 'Targets',
  'nav.specialistes': 'Specialists',
  'nav.documentation': 'Documentation',
  'nav.perfGroup': 'Performance Review',

  // Dashboard
  'dashboard.title': 'COA Performance Review',
  'dashboard.lastUpdate': 'Last update',
  'dashboard.monthlyData': 'Monthly Data',
  'dashboard.sector': 'Sector',
  'dashboard.global': 'Global',
  'dashboard.trends': 'Monthly Trends',
  'dashboard.qualSummary': 'Qualitative Summary',

  // Sector view
  'sector.back': '← Back',
  'sector.history': 'History by Theme',
  'sector.clickHint': '(click to view KPIs)',
  'sector.showKpi': '▼ View KPIs',
  'sector.hideKpi': '▲ Hide KPIs',
  'sector.detailByKpi': 'Detail by KPI',
  'sector.exportPptx': '📊 Export PPTX',
  'sector.exportPdf': '📄 Export PDF',
  'sector.exporting': 'Exporting...',
  'sector.qualSections': 'Qualitative Sections',

  // Statuts
  'status.conforme': 'On Track',
  'status.aSurveiller': 'Monitor',
  'status.critique': 'Critical',

  // Qualitative
  'qual.enjeux': 'Current Issues',
  'qual.bonsCoups': 'Highlights',
  'qual.demandeAide': 'Help Requests',
  'qual.noEntries': 'No entries for this period.',

  // Documentation
  'doc.title': 'Documentation — Themes and KPIs',
  'doc.subtitle': 'This document presents the performance themes and key performance indicators (KPIs) used in the COA performance review.',
  'doc.overview': 'Overview',
  'doc.sectors': 'Sectors',
  'doc.themes': 'Themes',
  'doc.kpis': 'KPIs',
  'doc.legend': 'Status Legend',
  'doc.methodology': 'Calculation Methodology',

  // Months
  'months.january': 'January',
  'months.february': 'February',
  'months.march': 'March',
  'months.april': 'April',
  'months.may': 'May',
  'months.june': 'June',
  'months.july': 'July',
  'months.august': 'August',
  'months.september': 'September',
  'months.october': 'October',
  'months.november': 'November',
  'months.december': 'December',

  // Qualitative empty states
  'qual.enjeux.empty': 'No issues for this period.',
  'qual.bonsCoups.empty': 'No highlights for this period.',
  'qual.demandeAide.empty': 'No help requests for this period.',

  // Saisie
  'saisie.title': 'Data Entry',
  'saisie.sector': 'Sector',
  'saisie.month': 'Month',
  'saisie.week': 'Week',
  'saisie.save': 'Save',
  'saisie.saved': 'Saved',
  'saisie.value': 'Value',

  // Qualitative page
  'qual.title': 'Qualitative Sections',
  'qual.add': 'Add',
  'qual.edit': 'Edit',
  'qual.delete': 'Delete',
  'qual.titre': 'Title',
  'qual.description': 'Description',
  'qual.criticality': 'Criticality',
  'qual.urgency': 'Urgency',
  'qual.status': 'Status',
  'qual.theme': 'Theme',

  // User Management
  'users.title': 'User Management',
  'users.subtitle': 'Manage users and their Cognito groups',
  'users.addTitle': 'Add a user',
  'users.email': 'Email',
  'users.tempPassword': 'Temporary password',
  'users.add': '+ Add',
  'users.creating': 'Creating...',
  'users.count': 'Users',
  'users.status': 'Status',
  'users.groups': 'Groups',
  'users.createdAt': 'Created',
  'users.actions': 'Actions',
  'users.delete': 'Delete',
  'users.confirmDelete': 'Confirm deletion',
  'users.confirmDeleteMsg': 'Are you sure you want to delete',
  'users.irreversible': 'This action is irreversible.',
  'users.cancel': 'Cancel',
  'users.deleting': 'Deleting...',
  'users.emailPlaceholder': 'firstname.lastname@riotinto.com',
  'users.emailError': 'Email must end with @riotinto.com',
  'users.createSuccess': 'User created successfully',
  'users.createError': 'Error creating user',
  'users.statusConfirmed': 'Confirmed',
  'users.statusForceChange': 'Password Change',
  'users.statusUnconfirmed': 'Unconfirmed',
  'users.addGroup': '+ Group',
  'users.assignGroup': 'Assign',
  'users.userExists': 'This user already exists',

  // Definitions
  'definitions.selectSector': 'Please select a sector to view KPI definitions.',

  // Adhesion Matrix — Navigation
  'adhesion.nav.group': 'Adhesion Matrix',
  'adhesion.nav.global': 'Global Matrix',
  'adhesion.nav.sector': 'Matrix by Sector',
  'adhesion.nav.evaluation': 'Evaluation',
  'adhesion.nav.lastEval': 'Last Evaluation',
  'adhesion.nav.documentation': 'Documentation',
  'adhesion.nav.actions': 'Actions in Progress',
  'adhesion.nav.admin': 'Administration',

  // Adhesion Matrix — Global
  'adhesion.global.title': 'COA Adhesion Matrix',
  'adhesion.global.period': 'Evaluation Period',
  'adhesion.global.print': 'Print',
  'adhesion.global.level3': 'Level 3',
  'adhesion.global.level2': 'Level 2',
  'adhesion.global.level1': 'Level 1',
  'adhesion.global.level0': 'Level 0',
  'adhesion.global.notEval': 'Not Evaluated',
  'adhesion.global.legend': 'Criteria Legend',

  // Adhesion Matrix — Sector
  'adhesion.sector.title': 'Adhesion Matrix by Sector',
  'adhesion.sector.selectSector': 'Select a sector',
  'adhesion.sector.selectHint': 'Choose a sector above to view the adhesion matrix by site.',
  'adhesion.sector.site': 'Site',
  'adhesion.sector.scoreByAxis': 'Score by axis',

  // Adhesion Matrix — Evaluation
  'adhesion.eval.title': 'Adhesion Evaluation',
  'adhesion.eval.save': 'Save',
  'adhesion.eval.saving': 'Saving...',
  'adhesion.eval.saved': 'Saved',
  'adhesion.eval.selectHint': 'Select a sector and period to start the evaluation.',
  'adhesion.eval.addComment': 'Add a comment',
  'adhesion.eval.comment': 'Comment',
  'adhesion.eval.cancel': 'Cancel',
  'adhesion.eval.register': 'Save',

  // Adhesion Matrix — Last Evaluation
  'adhesion.lastEval.title': 'Last Evaluation',
  'adhesion.lastEval.allSectors': 'All sectors',
  'adhesion.lastEval.thisMonth': 'This month',
  'adhesion.lastEval.months': 'months',
  'adhesion.lastEval.loading': 'Loading...',

  // Adhesion Matrix — Actions
  'adhesion.actions.title': 'Actions in Progress',
  'adhesion.actions.subtitle': 'Tracking actions by sector and adhesion domain',
  'adhesion.actions.allSectors': 'All sectors',
  'adhesion.actions.inProgress': 'actions in progress',
  'adhesion.actions.showCompleted': 'Show completed',
  'adhesion.actions.hideCompleted': 'Hide completed',
  'adhesion.actions.newAction': '+ New Action',
  'adhesion.actions.add': '+ Add',
  'adhesion.actions.empty': 'No active actions for this sector.',
  'adhesion.actions.edit': 'Edit',
  'adhesion.actions.delete': 'Delete this action?',
  'adhesion.actions.form.title': 'New Action',
  'adhesion.actions.form.editTitle': 'Edit Action',
  'adhesion.actions.form.sector': 'Sector (group)',
  'adhesion.actions.form.site': 'Site (specific)',
  'adhesion.actions.form.axis': 'Axis (domain)',
  'adhesion.actions.form.actionTitle': 'Title',
  'adhesion.actions.form.description': 'Description',
  'adhesion.actions.form.responsible': 'Responsible',
  'adhesion.actions.form.deadline': 'Deadline',
  'adhesion.actions.form.priority': 'Priority',
  'adhesion.actions.form.status': 'Status',
  'adhesion.actions.form.save': 'Save',
  'adhesion.actions.form.cancel': 'Cancel',
  'adhesion.actions.priority.low': 'Low',
  'adhesion.actions.priority.medium': 'Medium',
  'adhesion.actions.priority.high': 'High',
  'adhesion.actions.status.new': 'New',
  'adhesion.actions.status.inProgress': 'In Progress',
  'adhesion.actions.status.toConfirm': 'To Confirm',
  'adhesion.actions.status.resolved': 'Resolved',

  // Adhesion Matrix — Admin
  'adhesion.admin.title': 'Administration — Adhesion Matrix',
  'adhesion.admin.subtitle': 'Manage sites, sectors, periods and global matrix layout.',
  'adhesion.admin.sites': 'Sites',
  'adhesion.admin.sectors': 'Sectors',
  'adhesion.admin.periods': 'Periods',
  'adhesion.admin.sections': 'Matrix Sections',
  'adhesion.admin.add': '+ Add',
  'adhesion.admin.delete': 'Delete',
  'adhesion.admin.confirmDelete': 'Confirm Deletion',
  'adhesion.admin.deleteWarning': 'This action is irreversible and will delete all associated data.',
  'adhesion.admin.save': 'Save layout',
  'adhesion.admin.saved': 'Saved',
  'adhesion.admin.newPeriod': '+ New Period',
  'adhesion.admin.close': 'Close',
  'adhesion.admin.open': 'Open',
  'adhesion.admin.closed': 'Closed',

  // Product Review Module
  'productReview.nav.group': 'Product Perf. Review',
  'productReview.nav.dashboard': 'Dashboard',
  'productReview.nav.saisie': 'Data Entry',
  'productReview.nav.qualitatif': 'Qualitative',
  'productReview.nav.cibles': 'Targets',
  'productReview.nav.definitions': 'KPI Definitions',
  'productReview.nav.documentation': 'Documentation',
  'productReview.dashboard.title': 'Product Performance Review',
  'productReview.product': 'Product',
  'productReview.theme': 'Theme',
  'productReview.trends': 'Monthly Trends by Theme',
  'productReview.monthlyData': 'Monthly Data',
  'productReview.back': '← Back',
  'productReview.scoresByTheme': 'Scores by Theme',
  'productReview.detailByKpi': 'Detail by KPI',
  'productReview.scoresByProduct': 'Scores by Product',
  'productReview.trendsByProduct': 'Trends by Product',
  'productReview.dataEntry.title': 'Monthly Data Entry',
  'productReview.targets.title': 'Target Management',
  'productReview.definitions.title': 'KPI Definitions',
  'productReview.qualitative.title': 'Qualitative Data',
  'productReview.documentation.title': 'Documentation — Product Performance Review',
};

const IS: Translations = {
  // Navigation
  'nav.dashboard': 'Mælaborð',
  'nav.saisie': 'Gagnaskráning',
  'nav.qualitatif': 'Eigindlegt',
  'nav.cibles': 'Markmið',
  'nav.specialistes': 'Sérfræðingar',
  'nav.documentation': 'Skjölun',
  'nav.perfGroup': 'Árangursmat',

  // Dashboard
  'dashboard.title': 'COA árangursmat',
  'dashboard.lastUpdate': 'Síðast uppfært',
  'dashboard.monthlyData': 'Mánaðarleg gögn',
  'dashboard.sector': 'Svið',
  'dashboard.global': 'Heild',
  'dashboard.trends': 'Mánaðarlegar þróanir',
  'dashboard.qualSummary': 'Eigindleg samantekt',

  // Sector view
  'sector.back': '← Til baka',
  'sector.history': 'Saga eftir þema',
  'sector.clickHint': '(smelltu til að sjá KPI)',
  'sector.showKpi': '▼ Sjá KPI',
  'sector.hideKpi': '▲ Fela KPI',
  'sector.detailByKpi': 'Sundurliðun eftir KPI',
  'sector.exportPptx': '📊 Flytja út PPTX',
  'sector.exportPdf': '📄 Flytja út PDF',
  'sector.exporting': 'Flytur út...',
  'sector.qualSections': 'Eigindlegir hlutar',

  // Statuts
  'status.conforme': 'Á réttu gengi',
  'status.aSurveiller': 'Fylgjast með',
  'status.critique': 'Alvarlegt',

  // Qualitative
  'qual.enjeux': 'Yfirstandandi mál',
  'qual.bonsCoups': 'Góðir hlutir',
  'qual.demandeAide': 'Beiðni um aðstoð',
  'qual.noEntries': 'Engar færslur fyrir þetta tímabil.',

  // Documentation
  'doc.title': 'Skjölun — Þemu og KPI',
  'doc.subtitle': 'Þetta skjal kynnir árangursþemu og lykilmælikvarða (KPI) sem notaðir eru í COA árangursmati.',
  'doc.overview': 'Yfirlit',
  'doc.sectors': 'Svið',
  'doc.themes': 'Þemu',
  'doc.kpis': 'KPI',
  'doc.legend': 'Stöðuskýring',
  'doc.methodology': 'Útreikningsaðferð',

  // Months
  'months.january': 'Janúar',
  'months.february': 'Febrúar',
  'months.march': 'Mars',
  'months.april': 'Apríl',
  'months.may': 'Maí',
  'months.june': 'Júní',
  'months.july': 'Júlí',
  'months.august': 'Ágúst',
  'months.september': 'September',
  'months.october': 'Október',
  'months.november': 'Nóvember',
  'months.december': 'Desember',

  // Qualitative empty states
  'qual.enjeux.empty': 'Engin mál fyrir þetta tímabil.',
  'qual.bonsCoups.empty': 'Engir góðir hlutir fyrir þetta tímabil.',
  'qual.demandeAide.empty': 'Engar beiðnir um aðstoð fyrir þetta tímabil.',

  // Saisie
  'saisie.title': 'Gagnaskráning',
  'saisie.sector': 'Svið',
  'saisie.month': 'Mánuður',
  'saisie.week': 'Vika',
  'saisie.save': 'Vista',
  'saisie.saved': 'Vistað',
  'saisie.value': 'Gildi',

  // Qualitative page
  'qual.title': 'Eigindlegir hlutar',
  'qual.add': 'Bæta við',
  'qual.edit': 'Breyta',
  'qual.delete': 'Eyða',
  'qual.titre': 'Titill',
  'qual.description': 'Lýsing',
  'qual.criticality': 'Alvarleiki',
  'qual.urgency': 'Brýni',
  'qual.status': 'Staða',
  'qual.theme': 'Þema',

  // User Management
  'users.title': 'Notendastjórnun',
  'users.subtitle': 'Stjórna notendum og Cognito hópum þeirra',
  'users.addTitle': 'Bæta við notanda',
  'users.email': 'Netfang',
  'users.tempPassword': 'Tímabundið lykilorð',
  'users.add': '+ Bæta við',
  'users.creating': 'Bý til...',
  'users.count': 'Notendur',
  'users.status': 'Staða',
  'users.groups': 'Hópar',
  'users.createdAt': 'Búið til',
  'users.actions': 'Aðgerðir',
  'users.delete': 'Eyða',
  'users.confirmDelete': 'Staðfesta eyðingu',
  'users.confirmDeleteMsg': 'Ertu viss um að þú viljir eyða',
  'users.irreversible': 'Þessi aðgerð er óafturkræf.',
  'users.cancel': 'Hætta við',
  'users.deleting': 'Eyði...',
  'users.emailPlaceholder': 'fornafn.eftirnafn@riotinto.com',
  'users.emailError': 'Netfang verður að enda á @riotinto.com',
  'users.createSuccess': 'Notandi búinn til',
  'users.createError': 'Villa við að búa til notanda',
  'users.statusConfirmed': 'Staðfest',
  'users.statusForceChange': 'Lykilorðsbreyting',
  'users.statusUnconfirmed': 'Óstaðfest',
  'users.addGroup': '+ Hópur',
  'users.assignGroup': 'Úthluta',
  'users.userExists': 'Þessi notandi er þegar til',

  // Definitions
  'definitions.selectSector': 'Vinsamlegast veldu svið til að sjá KPI skilgreiningar.',

  // Adhesion Matrix — Navigation
  'adhesion.nav.group': 'Aðhaldsfylki',
  'adhesion.nav.global': 'Heildarfylki',
  'adhesion.nav.sector': 'Fylki eftir sviði',
  'adhesion.nav.evaluation': 'Mat',
  'adhesion.nav.lastEval': 'Síðasta mat',
  'adhesion.nav.documentation': 'Skjölun',
  'adhesion.nav.actions': 'Aðgerðir í gangi',
  'adhesion.nav.admin': 'Stjórnun',

  // Adhesion Matrix — Global
  'adhesion.global.title': 'COA aðhaldsfylki',
  'adhesion.global.period': 'Matstímabil',
  'adhesion.global.print': 'Prenta',
  'adhesion.global.level3': 'Stig 3',
  'adhesion.global.level2': 'Stig 2',
  'adhesion.global.level1': 'Stig 1',
  'adhesion.global.level0': 'Stig 0',
  'adhesion.global.notEval': 'Ekki metið',
  'adhesion.global.legend': 'Skýring viðmiða',

  // Adhesion Matrix — Sector
  'adhesion.sector.title': 'Aðhaldsfylki eftir sviði',
  'adhesion.sector.selectSector': 'Veldu svið',
  'adhesion.sector.selectHint': 'Veldu svið hér að ofan til að sjá aðhaldsfylki eftir stað.',
  'adhesion.sector.site': 'Staður',
  'adhesion.sector.scoreByAxis': 'Stig eftir ás',

  // Adhesion Matrix — Evaluation
  'adhesion.eval.title': 'Aðhaldsmat',
  'adhesion.eval.save': 'Vista',
  'adhesion.eval.saving': 'Vista...',
  'adhesion.eval.saved': 'Vistað',
  'adhesion.eval.selectHint': 'Veldu svið og tímabil til að hefja mat.',
  'adhesion.eval.addComment': 'Bæta við athugasemd',
  'adhesion.eval.comment': 'Athugasemd',
  'adhesion.eval.cancel': 'Hætta við',
  'adhesion.eval.register': 'Vista',

  // Adhesion Matrix — Last Evaluation
  'adhesion.lastEval.title': 'Síðasta mat',
  'adhesion.lastEval.allSectors': 'Öll svið',
  'adhesion.lastEval.thisMonth': 'Þessi mánuður',
  'adhesion.lastEval.months': 'mánuðir',
  'adhesion.lastEval.loading': 'Hleður...',

  // Adhesion Matrix — Actions
  'adhesion.actions.title': 'Aðgerðir í gangi',
  'adhesion.actions.subtitle': 'Eftirfylgni aðgerða eftir sviði og aðhaldssvið',
  'adhesion.actions.allSectors': 'Öll svið',
  'adhesion.actions.inProgress': 'aðgerðir í gangi',
  'adhesion.actions.showCompleted': 'Sýna kláraðar',
  'adhesion.actions.hideCompleted': 'Fela kláraðar',
  'adhesion.actions.newAction': '+ Ný aðgerð',
  'adhesion.actions.add': '+ Bæta við',
  'adhesion.actions.empty': 'Engar virkar aðgerðir fyrir þetta svið.',
  'adhesion.actions.edit': 'Breyta',
  'adhesion.actions.delete': 'Eyða þessari aðgerð?',
  'adhesion.actions.form.title': 'Ný aðgerð',
  'adhesion.actions.form.editTitle': 'Breyta aðgerð',
  'adhesion.actions.form.sector': 'Svið (hópur)',
  'adhesion.actions.form.site': 'Staður (sértækur)',
  'adhesion.actions.form.axis': 'Ás (svið)',
  'adhesion.actions.form.actionTitle': 'Titill',
  'adhesion.actions.form.description': 'Lýsing',
  'adhesion.actions.form.responsible': 'Ábyrgðaraðili',
  'adhesion.actions.form.deadline': 'Frestur',
  'adhesion.actions.form.priority': 'Forgangur',
  'adhesion.actions.form.status': 'Staða',
  'adhesion.actions.form.save': 'Vista',
  'adhesion.actions.form.cancel': 'Hætta við',
  'adhesion.actions.priority.low': 'Lágur',
  'adhesion.actions.priority.medium': 'Miðlungs',
  'adhesion.actions.priority.high': 'Hár',
  'adhesion.actions.status.new': 'Nýtt',
  'adhesion.actions.status.inProgress': 'Í gangi',
  'adhesion.actions.status.toConfirm': 'Til staðfestingar',
  'adhesion.actions.status.resolved': 'Leyst',

  // Adhesion Matrix — Admin
  'adhesion.admin.title': 'Stjórnun — Aðhaldsfylki',
  'adhesion.admin.subtitle': 'Stjórna stöðum, sviðum, tímabilum og uppsetningu fylkis.',
  'adhesion.admin.sites': 'Staðir',
  'adhesion.admin.sectors': 'Svið',
  'adhesion.admin.periods': 'Tímabil',
  'adhesion.admin.sections': 'Fylkishlutar',
  'adhesion.admin.add': '+ Bæta við',
  'adhesion.admin.delete': 'Eyða',
  'adhesion.admin.confirmDelete': 'Staðfesta eyðingu',
  'adhesion.admin.deleteWarning': 'Þessi aðgerð er óafturkræf og mun eyða öllum tengdum gögnum.',
  'adhesion.admin.save': 'Vista uppsetningu',
  'adhesion.admin.saved': 'Vistað',
  'adhesion.admin.newPeriod': '+ Nýtt tímabil',
  'adhesion.admin.close': 'Loka',
  'adhesion.admin.open': 'Opið',
  'adhesion.admin.closed': 'Lokað',

  // Product Review Module
  'productReview.nav.group': 'Vöruárangur',
  'productReview.nav.dashboard': 'Mælaborð',
  'productReview.nav.saisie': 'Gagnaskráning',
  'productReview.nav.qualitatif': 'Eigindlegt',
  'productReview.nav.cibles': 'Markmið',
  'productReview.nav.definitions': 'KPI skilgreiningar',
  'productReview.nav.documentation': 'Skjölun',
  'productReview.dashboard.title': 'Árangursmat vöru',
  'productReview.product': 'Vara',
  'productReview.theme': 'Þema',
  'productReview.trends': 'Mánaðarlegar þróanir eftir þema',
  'productReview.monthlyData': 'Mánaðarleg gögn',
  'productReview.back': '← Til baka',
  'productReview.scoresByTheme': 'Stig eftir þema',
  'productReview.detailByKpi': 'Sundurliðun eftir KPI',
  'productReview.scoresByProduct': 'Stig eftir vöru',
  'productReview.trendsByProduct': 'Þróanir eftir vöru',
  'productReview.dataEntry.title': 'Mánaðarleg gagnaskráning',
  'productReview.targets.title': 'Markmiðsstjórnun',
  'productReview.definitions.title': 'KPI skilgreiningar',
  'productReview.qualitative.title': 'Eigindleg gögn',
  'productReview.documentation.title': 'Skjölun — Árangursmat vöru',
};

const ALL_TRANSLATIONS: Record<Lang, Translations> = { fr: FR, en: EN, is: IS };

@Injectable({ providedIn: 'root' })
export class I18nService {
  private currentLang$ = new BehaviorSubject<Lang>(this.getStoredLang());
  lang$ = this.currentLang$.asObservable();

  private dataTranslations: Record<Lang, Record<string, string>> = {
    fr: {},
    en: {
      // Theme names
      "Main d'œuvre": 'Workforce',
      'Relation client': 'Client Relations',
      'Impact': 'Impact',
      'Outil': 'Tools',
      'Spécifique secteur': 'Sector Specific',
      // Sector names
      'Carbone': 'Carbon',
      'Électrolyse': 'Electrolysis',
      'CTG': 'CTG',
      'Coulée': 'Casting',
      'Vaudreuil': 'Vaudreuil',
      'Maintenance': 'Maintenance',
      'Environnement / Eau': 'Environment / Water',
      'Métal': 'Metal',
      // KPI names
      'Motivation': 'Motivation',
      'Bris de service': 'Service Breaks',
      'Irritants': 'Irritants',
      'Qualité des rituels': 'Ritual Quality',
      'Nombre de plaintes': 'Number of Complaints',
      'Formation client': 'Client Training',
      'Expérience client': 'Client Experience',
      'Visites usine': 'Plant Visits',
      'Documentation interventions': 'Intervention Documentation',
      'Ratio B4 / B2': 'B4/B2 Ratio',
      "Taux d'assignation": 'Assignment Rate',
      'Suivi RP sur thème': 'PR Follow-up by Theme',
      'Incidents production': 'Production Incidents',
      'Stabilité systèmes': 'System Stability',
      'Conformité délai Pattern': 'Pattern Delay Compliance',
      'Rythme livraison Heat Map': 'Heat Map Delivery Pace',
      // Qualitative labels
      'Enjeux': 'Issues',
      'Bons coups': 'Highlights',
      "Demandes d'aide": 'Help Requests',
      'Élevé': 'High',
      'Moyen': 'Medium',
      'Faible': 'Low',
      'Éléments prioritaires': 'Priority Items',
      'Enjeu': 'Issue',
      "Demande d'aide": 'Help Request',
      // Status badge
      'CONFORME': 'ON TRACK',
      'À SURVEILLER': 'MONITOR',
      'CRITIQUE': 'CRITICAL',
      'Conforme': 'On Track',
      'À surveiller': 'Monitor',
      'Critique': 'Critical',
      // Months short
      'Janv': 'Jan', 'Fév': 'Feb', 'Mars': 'Mar', 'Avr': 'Apr',
      'Mai': 'May', 'Juin': 'Jun', 'Juil': 'Jul', 'Août': 'Aug',
      'Sept': 'Sep', 'Oct': 'Oct', 'Nov': 'Nov', 'Déc': 'Dec',
      // Adhesion matrix - Sites (additional)
      'Environnement': 'Environment',
      'EEQ': 'EEQ',
      // Adhesion matrix - Axes
      'Adoption des principes directeurs COA dans son secteur': 'Adoption of COA guiding principles',
      'Capitalisation des apprentissages pour mettre des cales': 'Capitalizing on lessons learned',
      'Suivi de la conformité': 'Compliance monitoring',
      'Adoption': 'Adoption',
      'Capitalisation': 'Capitalization',
      'Conformité': 'Compliance',
    },
    is: {
      // Theme names
      "Main d'œuvre": 'Vinnuafl',
      'Relation client': 'Viðskiptasamband',
      'Impact': 'Áhrif',
      'Outil': 'Verkfæri',
      'Spécifique secteur': 'Sértækt svið',
      // Sector names
      'Carbone': 'Kolefni',
      'Électrolyse': 'Rafgreining',
      'CTG': 'CTG',
      'Coulée': 'Steypuvinnsla',
      'Vaudreuil': 'Vaudreuil',
      'Maintenance': 'Viðhald',
      'Environnement / Eau': 'Umhverfi / Vatn',
      'Métal': 'Málmur',
      // KPI names
      'Motivation': 'Hvatning',
      'Bris de service': 'Þjónusturof',
      'Irritants': 'Erting',
      'Qualité des rituels': 'Gæði helgisiða',
      'Nombre de plaintes': 'Fjöldi kvartana',
      'Formation client': 'Þjálfun viðskiptavina',
      'Expérience client': 'Upplifun viðskiptavina',
      'Visites usine': 'Verksmiðjuheimsóknir',
      'Documentation interventions': 'Skjölun inngripa',
      'Ratio B4 / B2': 'B4/B2 hlutfall',
      "Taux d'assignation": 'Úthlutunarhlutfall',
      'Suivi RP sur thème': 'Eftirfylgni RP eftir þema',
      'Incidents production': 'Framleiðsluatvik',
      'Stabilité systèmes': 'Stöðugleiki kerfa',
      'Conformité délai Pattern': 'Samræmi mynsturs tíma',
      'Rythme livraison Heat Map': 'Hraði hitakorts afhendingar',
      // Qualitative labels
      'Enjeux': 'Mál',
      'Bons coups': 'Góðir hlutir',
      "Demandes d'aide": 'Beiðnir um aðstoð',
      'Élevé': 'Hátt',
      'Moyen': 'Miðlungs',
      'Faible': 'Lágt',
      'Éléments prioritaires': 'Forgangsmál',
      'Enjeu': 'Mál',
      "Demande d'aide": 'Beiðni um aðstoð',
      // Status badge
      'CONFORME': 'Á RÉTTU GENGI',
      'À SURVEILLER': 'FYLGJAST MEÐ',
      'CRITIQUE': 'ALVARLEGT',
      'Conforme': 'Á réttu gengi',
      'À surveiller': 'Fylgjast með',
      'Critique': 'Alvarlegt',
      // Months short
      'Janv': 'Jan', 'Fév': 'Feb', 'Mars': 'Mar', 'Avr': 'Apr',
      'Mai': 'Maí', 'Juin': 'Jún', 'Juil': 'Júl', 'Août': 'Ágú',
      'Sept': 'Sep', 'Oct': 'Okt', 'Nov': 'Nóv', 'Déc': 'Des',
      // Adhesion matrix - Sites (additional)
      'Environnement': 'Umhverfi',
      'EEQ': 'EEQ',
      // Adhesion matrix - Axes
      'Adoption des principes directeurs COA dans son secteur': 'Innleiðing leiðarljósa COA',
      'Capitalisation des apprentissages pour mettre des cales': 'Nýting lærdóms til úrbóta',
      'Suivi de la conformité': 'Eftirlit með samræmi',
      'Adoption': 'Innleiðing',
      'Capitalisation': 'Nýting',
      'Conformité': 'Samræmi',
    },
  };

  get lang(): Lang {
    return this.currentLang$.value;
  }

  setLang(lang: Lang): void {
    this.currentLang$.next(lang);
    localStorage.setItem('kpi-coa-lang', lang);
  }

  t(key: string): string {
    return ALL_TRANSLATIONS[this.lang][key] || ALL_TRANSLATIONS['fr'][key] || key;
  }

  td(frenchText: string): string {
    if (this.lang === 'fr') return frenchText;
    return this.dataTranslations[this.lang][frenchText] || frenchText;
  }

  private getStoredLang(): Lang {
    const stored = localStorage.getItem('kpi-coa-lang') as Lang;
    return stored && ['fr', 'en', 'is'].includes(stored) ? stored : 'fr';
  }
}
